import { type DatatableState } from "@/domain/view/models/datatable-state-model";

import { resolveColumnOrderFromUrl } from "../datatable-search-params/column-order-from-url";
import { resolveColumnVisibilityFromUrl } from "../datatable-search-params/column-visibility-from-url";
import { resolveGlobalFilterFromUrl } from "../datatable-search-params/global-filter-from-url";
import { resolveSectionColumnIdFromUrl } from "../datatable-search-params/section-column-id-from-url";
import { resolveShowColumnHeadersFromUrl } from "../datatable-search-params/show-column-headers-from-url";
import { resolveSortingFromUrl } from "../datatable-search-params/sorting-from-url";
import { resolveSubSectionColumnIdFromUrl } from "../datatable-search-params/subsection-column-id-from-url";
import { type DatatableColumn, DatatableRowData } from "../datatable-types";

const omitUndefinedEntries = <T extends object>(source: T): Partial<T> => {
  return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined)) as Partial<T>;
};

const resolveColumnFiltersFromUrl = <TData extends DatatableRowData>({
  searchParams,
  columns,
}: {
  searchParams: URLSearchParams;
  columns: DatatableColumn<TData>[];
}): DatatableState["columnFilters"] => {
  return columns.flatMap((column) => column.resolveStateFromUrlParams?.(searchParams)?.columnFilters ?? []);
};

export const resolveDatatableStateFromUrl = <TData extends DatatableRowData>({
  searchParams,
  columns,
  initialState,
}: {
  searchParams: URLSearchParams;
  columns: DatatableColumn<TData>[];
  initialState: DatatableState;
}): DatatableState => {
  // sectionColumnId and subSectionColumnId resolve to null when the url clears them, so absence
  // has to be tested as undefined rather than with ??.
  const stateFromUrl = omitUndefinedEntries({
    showColumnHeaders: resolveShowColumnHeadersFromUrl(searchParams),
    sectionColumnId: resolveSectionColumnIdFromUrl(searchParams, columns),
    subSectionColumnId: resolveSubSectionColumnIdFromUrl(searchParams, columns),
    globalFilter: resolveGlobalFilterFromUrl(searchParams),
    columnOrder: resolveColumnOrderFromUrl(searchParams, columns),
    columnVisibility: resolveColumnVisibilityFromUrl(searchParams, columns),
    sorting: resolveSortingFromUrl(searchParams, columns),
  });

  const columnFiltersFromUrl = resolveColumnFiltersFromUrl({ searchParams, columns });

  return {
    ...initialState,
    ...stateFromUrl,
    ...(columnFiltersFromUrl.length > 0 ? { columnFilters: columnFiltersFromUrl } : {}),
  };
};
